/**
 * What a chart draws while its data is on its way: its own outline, with no values in it. A board keeps its shape from
 * the first paint, no chart says "No data" before the answer is in, and the data lands where the outline was. There is
 * one shape for each way the widgets draw, and a chart's own `state: loading` draws the same shape.
 *
 * The axes stand where Recharts puts the real chart's axes (the insets below, in pixels). Every number here is fixed:
 * an outline is the same on each render, and says nothing about the data to come.
 */
import { Fragment, type CSSProperties, type ReactNode } from "react";
import { ImageIcon } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { GAUGE_END, GAUGE_START, arcPath, gaugeLayout } from "./kpi/gauge-arc";

export type SkeletonShape =
  | "line"
  | "area"
  | "kpi"
  | "gauge"
  | "pareto"
  | "histogram"
  | "spc"
  | "scatter"
  | "heatmap"
  | "table"
  | "status-grid"
  | "alarms"
  | "events"
  | "equipment"
  | "tiles"
  | "image";

/** What a screen reader says for each shape. */
const WORDS: Record<SkeletonShape, string> = {
  line: "Loading the chart",
  area: "Loading the chart",
  kpi: "Loading the value",
  gauge: "Loading the gauge",
  pareto: "Loading the chart",
  histogram: "Loading the chart",
  spc: "Loading the control chart",
  scatter: "Loading the chart",
  heatmap: "Loading the correlations",
  table: "Loading the table",
  "status-grid": "Loading the status",
  alarms: "Loading the alarms",
  events: "Loading the activity",
  equipment: "Loading the equipment",
  tiles: "Loading the readings",
  image: "Loading the image",
};

const FILL = "bg-on-surface-muted/15";
const MARK = "bg-on-surface-muted/25";
const STROKE = "text-on-surface-muted/30";

interface Inset {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

// The real charts' margins and axes: 48-px or 60-px value axes, 30-px time axes, a 28-px legend.
const LEGEND = 28;
const LINE: Inset = { top: 8, right: 12, bottom: 38, left: 56 };
const SPC: Inset = { top: 8, right: 12, bottom: 34, left: 68 };
const HISTOGRAM: Inset = { top: 8, right: 12, bottom: 54, left: 68 };
const SCATTER: Inset = { top: 12, right: 24, bottom: 66, left: 84 };
const PARETO: Inset = { top: 12, right: 84, bottom: 93, left: 72 };

const WAVES = [
  "M0 62 C 7 48, 12 40, 20 46 S 33 72, 43 62 S 58 28, 68 34 S 86 58, 100 30",
  "M0 38 C 9 50, 15 58, 24 52 S 38 28, 48 34 S 64 62, 74 56 S 90 38, 100 48",
  "M0 78 C 8 72, 16 66, 26 72 S 42 84, 52 76 S 70 62, 80 68 S 94 78, 100 72",
];
const GRID = [0, 1, 2, 3, 4];
const TIME_LABELS = [0, 1, 2, 3, 4, 5];
const BELL = [8, 15, 28, 48, 72, 92, 100, 84, 60, 38, 22, 12, 6];
const PARETO_BARS = [94, 68, 48, 33, 23, 15, 10];
const PARETO_CUMULATIVE = [34, 58, 75, 85, 92, 97, 100];
const CONTROL_RUN = [52, 42, 58, 38, 64, 47, 34, 55, 44, 62, 40, 50, 36, 57, 46, 53];
const CLOUD: Array<[x: number, y: number]> = [
  [6, 84], [11, 70], [15, 77], [20, 62], [24, 72], [29, 56], [33, 64], [37, 47], [41, 58], [46, 43], [50, 53],
  [54, 36], [58, 47], [63, 31], [67, 41], [71, 26], [75, 36], [80, 21], [84, 30], [89, 16], [93, 24],
];
const CORRELATIONS = [
  [0.08, 0.22, 0.12, 0.3, 0.1, 0.18],
  [0.22, 0.08, 0.26, 0.12, 0.2, 0.1],
  [0.12, 0.26, 0.08, 0.16, 0.28, 0.14],
  [0.3, 0.12, 0.16, 0.08, 0.12, 0.24],
  [0.1, 0.2, 0.28, 0.12, 0.08, 0.16],
  [0.18, 0.1, 0.14, 0.24, 0.16, 0.08],
];
const CELL_WIDTHS = [72, 48, 60, 36, 54];

/** The place of a word or a number. */
function Word({ className, style }: { className?: string; style?: CSSProperties }) {
  return <span aria-hidden className={cn("block h-2 rounded-sm", FILL, className)} style={style} />;
}

/** A chart's axes: the grid and the value labels, the labels under it, and the plot area for its marks. */
function Plot({
  inset,
  rightAxis,
  columns,
  categories,
  xTitle,
  children,
}: {
  inset: Inset;
  /** A second value axis, on the right. */
  rightAxis?: boolean;
  /** Grid lines down the plot too. */
  columns?: boolean;
  /** A label for each of this many categories, in place of a time axis's. */
  categories?: number;
  /** The axis's own name, under its labels. */
  xTitle?: boolean;
  children: ReactNode;
}) {
  const box = { top: inset.top, right: inset.right, bottom: inset.bottom, left: inset.left };
  const labels = { left: inset.left, right: inset.right, top: `calc(100% - ${inset.bottom - 10}px)` };
  return (
    <>
      <div aria-hidden className="absolute flex flex-col justify-between" style={box}>
        {GRID.map((row) => (
          <div key={row} className="relative border-t border-dashed border-border">
            <Word className="absolute right-full top-[-4px] mr-2 w-6" />
            {rightAxis && <Word className="absolute left-full top-[-4px] ml-2 w-5" />}
          </div>
        ))}
      </div>
      {columns && (
        <div aria-hidden className="absolute flex justify-between" style={box}>
          {GRID.map((column) => (
            <div key={column} className="border-l border-dashed border-border" />
          ))}
        </div>
      )}
      {categories ? (
        <div aria-hidden className="absolute flex" style={labels}>
          {Array.from({ length: categories }, (_, i) => (
            <div key={i} className="flex flex-1 justify-center">
              <Word className="w-8 -rotate-[20deg]" />
            </div>
          ))}
        </div>
      ) : (
        <div aria-hidden className="absolute flex justify-between" style={labels}>
          {TIME_LABELS.map((label) => (
            <Word key={label} className="w-8" />
          ))}
        </div>
      )}
      {xTitle && <Word className="absolute bottom-2 left-1/2 w-24 -translate-x-1/2" />}
      <div aria-hidden className="absolute" style={box}>
        {children}
      </div>
    </>
  );
}

/** A line drawn across its box, whatever the box's size: its stroke keeps its width. */
function Wave({ d, filled }: { d: string; filled?: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className={cn("absolute inset-0 size-full", STROKE)}
    >
      {filled && <path d={`${d} L100 100 L0 100 Z`} fill="currentColor" opacity={0.45} />}
      <path
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** A line through points given in percent of its box. */
function Polyline({ points, dashed }: { points: Array<[x: number, y: number]>; dashed?: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className={cn("absolute inset-0 size-full", STROKE)}
    >
      <polyline
        points={points.map(([x, y]) => `${x},${y}`).join(" ")}
        fill="none"
        stroke="currentColor"
        strokeWidth={dashed ? 1.5 : 2}
        strokeDasharray={dashed ? "4 3" : undefined}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** Dots in percent of their box: positioned, not drawn in a stretched picture, so they stay round. */
function Dots({ points, className }: { points: Array<[x: number, y: number]>; className: string }) {
  return (
    <>
      {points.map(([x, y]) => (
        <span
          key={`${x},${y}`}
          aria-hidden
          className={cn("absolute -translate-x-1/2 -translate-y-1/2 rounded-full", MARK, className)}
          style={{ left: `${x}%`, top: `${y}%` }}
        />
      ))}
    </>
  );
}

function Legend({ count }: { count: number }) {
  return (
    <div aria-hidden className="absolute inset-x-0 bottom-1 flex justify-center gap-3">
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="flex items-center gap-1">
          <span className={cn("h-0.5 w-3 rounded-full", MARK)} />
          <Word className="w-12" />
        </span>
      ))}
    </div>
  );
}

function LineShape({ lines, filled }: { lines: number; filled: boolean }) {
  const shown = Math.min(Math.max(lines, 1), WAVES.length);
  return (
    <>
      <Plot inset={lines > 1 ? { ...LINE, bottom: LINE.bottom + LEGEND } : LINE}>
        {WAVES.slice(0, shown).map((d) => (
          <Wave key={d} d={d} filled={filled} />
        ))}
      </Plot>
      {lines > 1 && <Legend count={shown} />}
    </>
  );
}

function KpiShape() {
  return (
    <div className="flex h-full min-h-0 flex-col gap-2 pt-1">
      <div className="flex items-baseline gap-1.5">
        <Word className="h-7 w-24 rounded-md" />
        <Word className="h-3 w-6" />
      </div>
      {/* Off a board a card has no set height: the line still needs room to draw. */}
      <div className="relative min-h-10 flex-1">
        <Wave d={WAVES[0]} />
      </div>
    </div>
  );
}

function GaugeShape({ size }: { size: number }) {
  const { cx, cy, r, height, labelY, minLabelX, maxLabelX } = gaugeLayout(size);
  const value = { width: size * 0.36, height: size * 0.15 };
  const label = { width: 18, height: 7 };
  return (
    <div className="flex h-full w-full items-center justify-center">
      <svg
        aria-hidden
        width={size}
        height={height}
        viewBox={`0 0 ${size} ${height}`}
        className={cn("max-h-full max-w-full", STROKE)}
      >
        <path
          d={arcPath(cx, cy, r, GAUGE_START, GAUGE_END)}
          stroke="currentColor"
          strokeWidth={8}
          strokeLinecap="round"
          fill="none"
        />
        <rect
          x={cx - value.width / 2}
          y={cy - value.height + 6}
          width={value.width}
          height={value.height}
          rx={4}
          fill="currentColor"
          opacity={0.7}
        />
        <rect x={cx - 12} y={cy + 18} width={24} height={8} rx={2} fill="currentColor" opacity={0.5} />
        {[minLabelX, maxLabelX - label.width].map((x) => (
          <rect
            key={x}
            x={x}
            y={labelY - label.height}
            width={label.width}
            height={label.height}
            rx={2}
            fill="currentColor"
            opacity={0.5}
          />
        ))}
      </svg>
    </div>
  );
}

function ParetoShape() {
  const middle = (i: number) => ((i + 0.5) / PARETO_BARS.length) * 100;
  const cumulative = PARETO_CUMULATIVE.map((pct, i): [number, number] => [middle(i), 100 - pct]);
  return (
    <>
      <Plot inset={PARETO} rightAxis categories={PARETO_BARS.length}>
        <div className="absolute inset-0 flex items-end gap-1.5 px-1">
          {PARETO_BARS.map((height) => (
            <div key={height} className={cn("flex-1 rounded-t-[3px]", MARK)} style={{ height: `${height}%` }} />
          ))}
        </div>
        <Polyline points={cumulative} />
        <Dots points={cumulative} className="size-1.5" />
      </Plot>
      <Legend count={2} />
    </>
  );
}

function HistogramShape() {
  return (
    <Plot inset={HISTOGRAM} xTitle>
      <div className="absolute inset-0 flex items-end gap-px">
        {BELL.map((height, i) => (
          <div key={i} className={cn("flex-1 rounded-t-[3px]", MARK)} style={{ height: `${height}%` }} />
        ))}
      </div>
      <div className="absolute inset-y-0 left-[20%] border-l border-dashed border-on-surface-muted/30" />
      <div className="absolute inset-y-0 right-[20%] border-l border-dashed border-on-surface-muted/30" />
    </Plot>
  );
}

function SpcShape() {
  const run = CONTROL_RUN.map((y, i): [number, number] => [(i / (CONTROL_RUN.length - 1)) * 100, y]);
  return (
    <div className="flex h-full flex-col gap-1">
      <div aria-hidden className="flex shrink-0 items-center gap-2">
        <Word className="h-4 w-20 rounded-md" />
        <Word className="w-14" />
        <Word className="ml-auto w-24" />
      </div>
      <div className="relative min-h-0 flex-1">
        <Plot inset={SPC}>
          <div className="absolute inset-x-0 top-[14%] border-t border-dashed border-on-surface-muted/35" />
          <div className="absolute inset-x-0 top-1/2 border-t border-on-surface-muted/35" />
          <div className="absolute inset-x-0 top-[86%] border-t border-dashed border-on-surface-muted/35" />
          <Polyline points={run} />
          <Dots points={run} className="size-1.5" />
        </Plot>
      </div>
    </div>
  );
}

function ScatterShape() {
  return (
    <Plot inset={SCATTER} columns xTitle>
      <Polyline
        points={[
          [3, 82],
          [97, 20],
        ]}
        dashed
      />
      <Dots points={CLOUD} className="size-2" />
    </Plot>
  );
}

function HeatmapShape() {
  return (
    <div aria-hidden className="inline-grid gap-0.5" style={{ gridTemplateColumns: `72px repeat(${CORRELATIONS.length}, 48px)` }}>
      <span />
      {CORRELATIONS.map((_, column) => (
        <div key={column} className="relative h-32">
          <Word className="absolute bottom-4 left-4 w-16 origin-bottom-left -rotate-45" />
        </div>
      ))}
      {CORRELATIONS.map((row, r) => (
        <Fragment key={r}>
          <div className="flex items-center justify-end pr-2">
            <Word className="w-12" />
          </div>
          {row.map((strength, column) => (
            <div key={column} className="size-12 rounded bg-on-surface-muted" style={{ opacity: strength }} />
          ))}
        </Fragment>
      ))}
    </div>
  );
}

function TableShape({ rows, columns, named }: { rows: number; columns: number; named: boolean }) {
  const cells = Array.from({ length: Math.max(columns, 1) }, (_, c) => c);
  return (
    <div aria-hidden className="overflow-hidden rounded-lg border border-border bg-surface-elevated">
      <div className="flex min-h-[33px] items-center gap-4 border-b border-border bg-surface-muted/40 px-3">
        {cells.map((c) => (
          <div key={c} className="min-w-0 flex-1">
            <Word className="w-14 max-w-full" />
          </div>
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div
          key={r}
          className={cn("flex items-center gap-4 border-b border-border px-3 last:border-b-0", named ? "min-h-[57px]" : "min-h-10")}
        >
          {cells.map((c) => (
            <div key={c} className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Word style={{ width: `${CELL_WIDTHS[(r + c) % CELL_WIDTHS.length]}%` }} />
              {named && c === 0 && <Word className="h-1.5 w-1/2" />}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function StatusGridShape({ cells }: { cells: number }) {
  return (
    <div aria-hidden className="grid h-full grid-cols-2 content-start gap-2">
      {Array.from({ length: cells }, (_, i) => (
        <div key={i} className="flex flex-col gap-1.5 rounded-md border border-border bg-surface-elevated p-2">
          <Word className="w-3/4" />
          <span className="flex items-center gap-1.5">
            <span className={cn("size-2 shrink-0 rounded-full", MARK)} />
            <Word className="h-2.5 w-10" />
          </span>
          <Word className="h-1.5 w-2/3" />
        </div>
      ))}
    </div>
  );
}

function AlarmsShape() {
  return (
    <div aria-hidden className="flex flex-col gap-2">
      {[0, 1, 2].map((alarm) => (
        <div key={alarm} className="flex gap-3 rounded-lg border border-border bg-surface-elevated p-3">
          <span className={cn("size-8 shrink-0 rounded-full", FILL)} />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <Word className="h-3 w-2/5" />
              <Word className="h-3 w-12 rounded-full" />
            </div>
            <Word className="w-1/3" />
            <Word className="w-4/5" />
            <Word className="h-1.5 w-1/4" />
          </div>
          <Word className="h-8 w-28 shrink-0 rounded-md" />
        </div>
      ))}
    </div>
  );
}

function EventsShape() {
  return (
    <ol aria-hidden className="flex flex-col">
      {[0, 1, 2, 3].map((event) => (
        <li
          key={event}
          className="relative flex gap-3 pb-4 before:absolute before:bottom-0 before:left-[15px] before:top-9 before:w-px before:bg-border last:before:hidden"
        >
          <span className={cn("size-8 shrink-0 rounded-full", FILL)} />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-1">
            <div className="flex items-center justify-between gap-2">
              <Word className="h-3 w-1/2" />
              <Word className="h-1.5 w-10" />
            </div>
            <Word className="w-1/3" />
          </div>
        </li>
      ))}
    </ol>
  );
}

function TilesShape({ tiles }: { tiles: number }) {
  return (
    <div aria-hidden className="grid auto-rows-min grid-cols-2 gap-1.5">
      {Array.from({ length: tiles }, (_, i) => (
        <div key={i} className="flex flex-col gap-1.5 rounded-md border border-border bg-surface-elevated px-2 py-1.5">
          <Word className="h-1.5 w-2/3" />
          <Word className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

function EquipmentShape({ tiles }: { tiles: number }) {
  return (
    <div className="flex h-full flex-col">
      <div aria-hidden className="mb-2 flex items-center gap-2">
        <span className={cn("size-7 shrink-0 rounded-md", FILL)} />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Word className="h-3 w-2/5" />
          <Word className="h-1.5 w-1/4" />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <TilesShape tiles={tiles} />
      </div>
    </div>
  );
}

function ImageShape() {
  return (
    <div aria-hidden className={cn("flex h-full items-center justify-center rounded-md", FILL)}>
      <ImageIcon className="size-8 text-on-surface-muted/40" strokeWidth={1.5} />
    </div>
  );
}

function clamped(count: number, none: number, most: number): number {
  return count > 0 ? Math.min(count, most) : none;
}

function drawn(shape: SkeletonShape, count: number, columns: number, size: number): ReactNode {
  switch (shape) {
    case "line":
    case "area":
      return <LineShape lines={count} filled={shape === "area"} />;
    case "kpi":
      return <KpiShape />;
    case "gauge":
      return <GaugeShape size={size} />;
    case "pareto":
      return <ParetoShape />;
    case "histogram":
      return <HistogramShape />;
    case "spc":
      return <SpcShape />;
    case "scatter":
      return <ScatterShape />;
    case "heatmap":
      return <HeatmapShape />;
    case "table":
      // A board's table reads tags: each row names its reading, and under it the machine.
      return <TableShape rows={clamped(count, 6, 12)} columns={columns} named={count > 0} />;
    case "status-grid":
      return <StatusGridShape cells={clamped(count, 4, 8)} />;
    case "alarms":
      return <AlarmsShape />;
    case "events":
      return <EventsShape />;
    case "equipment":
      return <EquipmentShape tiles={clamped(count, 6, 12)} />;
    case "tiles":
      return <TilesShape tiles={clamped(count, 6, 12)} />;
    case "image":
      return <ImageShape />;
  }
}

export interface ChartSkeletonProps {
  shape: SkeletonShape;
  /** How many the widget reads, where that shows: a chart's lines, a table's rows, a status grid's cells. */
  count?: number;
  /** A table's columns. */
  columns?: number;
  /** A gauge's size, as GaugeChart takes it. */
  size?: number;
  /** The height the chart draws at. Without it, the height of its parent. */
  height?: number;
  className?: string;
}

export function ChartSkeleton({ shape, count = 0, columns = 4, size = 180, height, className }: ChartSkeletonProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      data-skeleton={shape}
      className={cn("relative h-full w-full animate-pulse overflow-hidden", className)}
      style={height === undefined ? undefined : { height }}
    >
      <span className="sr-only">{WORDS[shape]}</span>
      {drawn(shape, count, columns, size)}
    </div>
  );
}

const BOARD_TILES = [
  "h-32 lg:col-span-3",
  "h-32 lg:col-span-3",
  "h-32 lg:col-span-3",
  "h-32 lg:col-span-3",
  "h-72 lg:col-span-6",
  "h-72 lg:col-span-6",
];

/** A board that is not read yet: the page's header and a grid of empty tiles. It does not know the board's widgets. */
export function BoardSkeleton() {
  return (
    <div role="status" aria-busy="true" data-skeleton="board" className="flex animate-pulse flex-col gap-6">
      <span className="sr-only">Loading the board</span>
      <div aria-hidden className="flex flex-col gap-3">
        <Word className="w-40" />
        <Word className="h-7 w-64 rounded-md" />
        <Word className="h-3 w-80 max-w-full" />
      </div>
      <div aria-hidden className="grid grid-cols-12 gap-4">
        {BOARD_TILES.map((tile, i) => (
          <div
            key={i}
            className={cn("col-span-12 flex flex-col gap-3 rounded-lg border border-border bg-surface-elevated p-4", tile)}
          >
            <Word className="h-3 w-1/3" />
            <div className={cn("min-h-0 flex-1 rounded-md", FILL)} />
          </div>
        ))}
      </div>
    </div>
  );
}
